<template>
  <div>
    <!-- Tabs (volle Breite) -->
    <nav
      class="tabs full-width"
      role="navigation"
      aria-label="Lernbriefe Navigation"
    >
      <button
        class="tab"
        :class="{active: activeTab === 'letters'}"
        @click="activeTab = 'letters'"
      >
        Lernbriefe
      </button>
      <button
        v-if="selectedLetter && selectedHasStudents"
        class="tab"
        :class="{active: activeTab === 'letterDetail'}"
        title="Lernbrief-Detail"
        aria-label="Lernbrief-Detail"
        @click="activeTab = 'letterDetail'"
      >
        Lernbrief – {{ selectedLetter?.title || '—' }}
      </button>
      <button
        v-if="selectedLetter && selectedHasStudents"
        class="tab"
        :class="{active: activeTab === 'templates'}"
        @click="activeTab = 'templates'"
      >
        Vorlagen / Rückmeldebögen
      </button>
      <button
        v-if="selectedLetter && selectedHasStudents"
        class="tab"
        :class="{active: activeTab === 'assessments'}"
        @click="activeTab = 'assessments'"
      >
        Kompetenzraster
      </button>
    </nav>

    <!-- Tab 1: Lernbriefe -->
    <section
      v-if="activeTab === 'letters'"
      class="card"
    >
      <header class="subheader">
        <h3>Lernbriefe</h3>
        <div class="d-flex gap-2">
          <button
            v-if="canManageLetters"
            class="btn btn-primary"
            @click="openNewLetter"
          >
            Neu
          </button>
        </div>
      </header>

      <div class="header-actions">
        <div class="filters letters">
          <input
            v-model="letterFilters.q"
            type="text"
            class="input"
            placeholder="Titel suchen"
            aria-label="Titel suchen"
            @keyup.enter="loadLetters(1)"
            @change="loadLetters(1)"
          >
          <button
            class="btn"
            @click="resetLetterFilters"
          >
            Reset
          </button>
        </div>
      </div>

      <div class="table">
        <div class="thead thead-letters">
          <div>Titel</div>
          <div>Erstellt</div>
          <div>Von</div>
          <div class="actions">
            Aktionen
          </div>
        </div>

        <div
          v-for="row in filteredLetters"
          :key="row.id"
          class="tr tr-letters"
        >
          <div>
            {{ row.title }}
            <span
              v-if="rowFinalized(row)"
              class="ml-2"
              title="Lernbrief ist gesperrt"
            >
              <i
                class="pi pi-lock"
                aria-hidden="true"
                style="font-size: 12px; color: steelblue;"
              />
            </span>
          </div>
          <div>{{ toLocal(row.created_at) }}</div>
          <div>{{ row.created_by_name }}</div>
          <div class="actions">
            <button
              v-if="canOpenLetter(row)"
              class="btn text-white btn-primary icon-only"
              type="button"
              title="Lernbrief öffnen"
              aria-label="Öffnen"
              style="width: auto; margin-right: 5px; font-size: 12px;"
              @click="openLetter(row)"
            >
              Öffnen
            </button>
            <button
              v-if="canManageLetters"
              :class="['btn', 'icon-only', rowFinalized(row) ? 'btn-success' : 'btn-primary']"
              type="button"
              :title="rowFinalized(row) ? 'Final zurücknehmen' : 'Als final markieren'"
              :aria-label="rowFinalized(row) ? 'Final zurücknehmen' : 'Als final markieren'"
              @click="toggleLetterFinal(row)"
            >
              <i
                :class="rowFinalized(row) ? 'pi pi-lock' : 'pi pi-lock-open'"
                aria-hidden="true"
              />
            </button>
            <button
              v-if="canManageLetters"
              class="btn btn-success icon-only"
              type="button"
              title="Schüler zuweisen"
              aria-label="Schüler zuweisen"
              :disabled="rowFinalized(row)"
              @click="openAssignStudents(row)"
            >
              <i
                class="pi pi-user"
                aria-hidden="true"
              />
            </button>
            <button
              v-if="canManageLetters"
              class="btn btn-success icon-only"
              type="button"
              title="Bearbeiten"
              aria-label="Bearbeiten"
              :disabled="rowFinalized(row)"
              @click="openEditLetter(row)"
            >
              <i
                class="pi pi-pencil"
                aria-hidden="true"
              />
            </button>
            <button
              v-if="canManageLetters"
              class="btn danger icon-only"
              type="button"
              title="Löschen"
              aria-label="Löschen"
              :disabled="rowFinalized(row)"
              @click="removeLetter(row)"
            >
              <i
                class="pi pi-trash"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <div
          v-if="filteredLetters.length === 0"
          class="empty"
        >
          Keine Daten
        </div>
      </div>

      <div class="pagination">
        <div class="pagination-left">
          <span class="meta">Gesamt: {{ letters.total }}</span>
        </div>

        <div class="pagination-right">
          <label class="meta page-size">
            <span>Einträge:</span>
            <select
              v-model.number="lettersPageSize"
              class="input input-select"
              aria-label="Einträge pro Seite (Lernbriefe)"
              @change="loadLetters(1)"
            >
              <option
                v-for="opt in pageSizeOptions"
                :key="opt"
                :value="opt"
              >{{ opt }}
              </option>
            </select>
          </label>

          <div class="pagination-center">
            <button
              class="btn"
              :disabled="letters.page <= 1"
              @click="loadLetters(letters.page - 1)"
            >
              Zurück
            </button>
            <span class="meta">Seite {{ letters.page }} / {{ letters.pages }}</span>
            <button
              class="btn"
              :disabled="letters.page >= letters.pages"
              @click="loadLetters(letters.page + 1)"
            >
              Weiter
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Tab 2: Lernbrief-Detail – Schülerliste -->
    <section
      v-if="activeTab === 'letterDetail'"
      class="card"
    >
      <header class="subheader">
        <h3>Lernbrief – {{ selectedLetter?.title }}</h3>
      </header>

      <div
        class="table table-students"
        :style="getStudentsTableVars()"
      >
        <div class="thead thead-students">
          <div>Schüler</div>
          <div class="text-center">
            Status
          </div>
          <div class="actions">
            Aktion
          </div>
        </div>

        <div
          v-for="s in visibleLetterStudents"
          :key="s.id"
          class="tr tr-students"
        >
          <div>
            {{ displayStudentName(s) }}
          </div>
          <div>
            <div
              class="status-timeline"
              role="group"
              aria-label="Status-Timeline"
            >
              <div
                class="track"
                :style="getTrackStyleFor()"
                aria-hidden="true"
              />
              <span
                v-for="(b, idx) in buildStatusBoxes(s)"
                :key="idx"
                :class="['box', b.kind, b.cls]"
                :title="b.title"
              >
                <span class="abbr">{{ b.label }}</span>
              </span>
            </div>
          </div>
          <div class="actions">
            <button
              class="btn icon-only"
              type="button"
              title="PDF erstellen"
              aria-label="PDF erstellen"
              style="color: lightslategray;"
              @click="onStudentPdf(s)"
            >
              <i
                class="pi pi-print"
                aria-hidden="true"
              />
            </button>
            <button
              v-if="canManageLetters"
              class="btn btn-success icon-only w-auto px-2"
              type="button"
              title="Vorblatt bearbeiten"
              aria-label="Vorblatt bearbeiten"
              :disabled="isSelectedLetterFinalized"
              @click="onEditFront(s)"
            >
              <i
                class="pi pi-pencil"
                aria-hidden="true"
              /> VB
            </button>
            <button
              v-if="canManageLetters"
              class="btn btn-success icon-only w-auto px-2"
              type="button"
              title="Rückblatt bearbeiten"
              aria-label="Rückblatt bearbeiten"
              :disabled="isSelectedLetterFinalized"
              @click="onEditBack(s)"
            >
              <i
                class="pi pi-pencil"
                aria-hidden="true"
              /> RB
            </button>
          </div>
        </div>

        <div
          v-if="visibleLetterStudents.length === 0"
          class="empty"
        >
          Keine Daten
        </div>
      </div>

      <div class="pagination">
        <div class="pagination-left">
          <span class="meta">Gesamt: {{ letterStudents.total }}</span>
        </div>

        <div class="pagination-right">
          <label class="meta page-size">
            <span>Einträge:</span>
            <select
              v-model.number="letterStudentsPageSize"
              class="input input-select"
              aria-label="Einträge pro Seite (Schüler)"
              @change="loadLetterStudents(1)"
            >
              <option
                v-for="opt in pageSizeOptions"
                :key="opt"
                :value="opt"
              >{{ opt }}
              </option>
            </select>
          </label>

          <div class="pagination-center">
            <button
              class="btn"
              :disabled="letterStudents.page <= 1"
              @click="loadLetterStudents(letterStudents.page - 1)"
            >
              Zurück
            </button>
            <span class="meta">Seite {{ letterStudents.page }} / {{ letterStudents.pages }}</span>
            <button
              class="btn"
              :disabled="letterStudents.page >= letterStudents.pages"
              @click="loadLetterStudents(letterStudents.page + 1)"
            >
              Weiter
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Tab 3: Vorlagen / Rückmeldebögen -->
    <div
      v-if="activeTab === 'templates' && selectedLetter"
    >
      <section class="col card">
        <header class="subheader">
          <h3>Vorlagen / Rückmeldebögen</h3>
          <div class="d-flex gap-2">
            <button
              class="btn btn-primary"
              @click="openNewTemplate"
            >
              Neu
            </button>
          </div>
        </header>

        <div class="header-actions">
          <div class="filters templates">
            <input
              v-model="templateFilters.topic"
              type="text"
              class="input"
              placeholder="Thema oder Notiz suchen"
              aria-label="Thema oder Notiz suchen"
              @input="onTemplateTopicInput"
            >
            <select
              v-model="templateFilters.scope"
              class="input input-select"
              aria-label="Bereich filtern"
              @change="loadTemplates(1)"
            >
              <option value="letter">
                in diesem Lernbrief
              </option>
              <option value="all">
                alle Vorlagen
              </option>
            </select>
            <select
              v-model.number="templateFilters.subjectId"
              class="input input-select"
              aria-label="Fach filtern"
              @change="loadTemplates(1)"
            >
              <option :value="null">
                Alle Fächer
              </option>
              <option
                v-for="opt in templateSubjectOptions"
                :key="opt.id"
                :value="opt.id"
              >
                {{ opt.label }}
              </option>
            </select>
            <select
              v-model.number="templateFilters.teacherId"
              class="input input-select"
              aria-label="Lehrer filtern"
              @change="loadTemplates(1)"
            >
              <option :value="null">
                Alle Lehrer
              </option>
              <option
                v-for="opt in templateTeacherOptions"
                :key="opt.id"
                :value="opt.id"
              >
                {{ opt.label }}
              </option>
            </select>
            <button
              class="btn"
              @click="resetTemplateFilters"
            >
              Reset
            </button>
          </div>
        </div>

        <div class="table">
          <div class="thead thead-templates">
            <div>Thema</div>
            <div>Notiz</div>
            <div>Fach</div>
            <div>Lehrer</div>
            <div>Erstellt</div>
            <div class="actions">
              Aktionen
            </div>
          </div>

          <div
            v-for="row in templates.items"
            :key="row.id"
            class="tr tr-templates"
          >
            <div>
              {{ row.topic }}
              <span
                v-if="templateOverlengthMap.get(Number(row.id)) === true"
                class="badge badge-warn"
                aria-label="Überlänge"
                title="Überlänge (passt voraussichtlich nicht ins Seitenlimit)"
              >
                Überlänge
              </span>
            </div>
            <div>{{ row.internal_note || '' }}</div>
            <div>{{ row.subject }}</div>
            <div>{{ row.teacher }}</div>
            <div>{{ toLocal(row.created_at) }}</div>
            <div class="actions">
              <button
                class="btn btn-success icon-only"
                type="button"
                title="Vorlage bearbeiten"
                aria-label="Vorlage bearbeiten"
                :disabled="isSelectedLetterFinalized || !canEditTemplateInSelectedLetter(row)"
                @click="openEditTemplate(row)"
              >
                <i
                  class="pi pi-pencil"
                  aria-hidden="true"
                />
              </button>
              <button
                class="btn icon-only"
                type="button"
                title="Rückmeldungsbogen drucken"
                aria-label="Rückmeldungsbogen drucken"
                style="color: lightslategray;"
                @click="openPrintForms(row)"
              >
                <i
                  class="pi pi-print"
                  aria-hidden="true"
                />
              </button>
              <button
                class="btn btn-success icon-only"
                type="button"
                title="Autom. Raster erzeugen"
                aria-label="Autom. Raster erzeugen"
                :disabled="isSelectedLetterFinalized || !canAutoCreateForTemplate(row) || autoCreateTplId === row.id"
                @click="autoCreateAssessmentsForTemplate(row)"
              >
                <i
                  class="pi pi-list"
                  aria-hidden="true"
                />
              </button>
              <button
                class="btn btn-success icon-only"
                type="button"
                title="Vorlage duplizieren"
                aria-label="Vorlage duplizieren"
                :disabled="isSelectedLetterFinalized"
                @click="onDuplicateTemplate(row.id)"
              >
                <i
                  class="pi pi-copy"
                  aria-hidden="true"
                />
              </button>
              <button
                class="btn danger icon-only"
                type="button"
                :disabled="isSelectedLetterFinalized || !canEditOrDeleteTemplate(row) || hasLinkedAssessments(row)"
                :title="hasLinkedAssessments(row)
                  ? 'Kann nicht gelöscht werden: verknüpfte Kompetenzraster vorhanden'
                  : 'Vorlage löschen'"
                aria-label="Vorlage löschen"
                @click="removeTemplate(row)"
              >
                <i
                  class="pi pi-trash"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          <div
            v-if="filteredTemplates.length === 0"
            class="empty"
          >
            Keine Daten
          </div>
        </div>

        <div class="pagination">
          <div class="pagination-left">
            <span class="meta">Gesamt: {{ templates.total }}</span>
          </div>

          <div class="pagination-right">
            <label class="meta page-size">
              <span>Einträge:</span>
              <select
                v-model.number="templatesPageSize"
                class="input input-select"
                aria-label="Einträge pro Seite (Vorlagen)"
                @change="loadTemplates(1)"
              >
                <option
                  v-for="opt in pageSizeOptions"
                  :key="opt"
                  :value="opt"
                >{{ opt }}
                </option>
              </select>
            </label>

            <div class="pagination-center">
              <button
                class="btn"
                :disabled="templates.page <= 1"
                @click="loadTemplates(templates.page - 1)"
              >
                Zurück
              </button>
              <span class="meta">Seite {{ templates.page }} / {{ templates.pages }}</span>
              <button
                class="btn"
                :disabled="templates.page >= templates.pages"
                @click="loadTemplates(templates.page + 1)"
              >
                Weiter
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Tab 3 rechte Spalte leer (optional für spätere Inhalte) -->
      <section class="col card d-none" />
    </div>

    <!-- Tab 4: Kompetenzraster -->
    <section
      v-if="activeTab === 'assessments' && selectedLetter"
      class="col card"
    >
      <header class="subheader">
        <h3>Kompetenzraster</h3>
        <div class="d-flex gap-2">
          <button
            class="btn btn-primary"
            @click="openNewAssessment"
          >
            Neu
          </button>
        </div>
      </header>

      <div class="header-actions">
        <div class="filters assessments">
          <input
            v-model="assessmentFilters.topic"
            type="text"
            class="input"
            placeholder="Thema oder Notiz suchen"
            aria-label="Thema oder Notiz suchen (Kompetenzraster)"
            @input="onAssessmentTopicInput"
          >
          <select
            v-model.number="assessmentFilters.subjectId"
            class="input input-select"
            aria-label="Fach filtern (Kompetenzraster)"
            @change="loadAssessments(1)"
          >
            <option :value="null">
              Alle Fächer
            </option>
            <option
              v-for="opt in assessmentSubjectOptions"
              :key="opt.id"
              :value="opt.id"
            >
              {{ opt.label }}
            </option>
          </select>
          <select
            v-model.number="assessmentFilters.teacherId"
            class="input input-select"
            aria-label="Lehrer filtern (Kompetenzraster)"
            @change="loadAssessments(1)"
          >
            <option :value="null">
              Alle Lehrer
            </option>
            <option
              v-for="opt in assessmentTeacherOptions"
              :key="opt.id"
              :value="opt.id"
            >
              {{ opt.label }}
            </option>
          </select>
          <select
            v-model.number="assessmentFilters.studentId"
            class="input input-select"
            aria-label="Schüler filtern (Kompetenzraster)"
            @change="loadAssessments(1)"
          >
            <option :value="null">
              Alle Schüler
            </option>
            <option
              v-for="opt in assessmentStudentOptions"
              :key="opt.id"
              :value="opt.id"
            >
              {{ opt.label }}
            </option>
          </select>
          <button
            class="btn"
            @click="resetAssessmentFilters"
          >
            Reset
          </button>
        </div>
      </div>

      <div class="table">
        <div class="thead thead-assessments">
          <div>Thema</div>
          <div>Notiz</div>
          <div>Fach</div>
          <div>Lehrer</div>
          <div>Schüler</div>
          <div>Datum</div>
          <div class="actions">
            Aktionen
          </div>
        </div>

        <div
          v-for="row in assessments.items"
          :key="row.id"
          class="tr tr-assessments"
        >
          <div>
            {{ row.topic }}
            <span
              v-if="row.released"
              class="badge badge-success"
              aria-label="Freigegeben"
              title="Freigegeben"
            >
              freigegeben
            </span>
          </div>
          <div>{{ row.internal_note || '' }}</div>
          <div>{{ row.subject }}</div>
          <div>{{ row.teacher }}</div>
          <div>{{ row.student }}</div>
          <div>{{ toLocal(row.date) }}</div>
          <div class="actions">
            <button
              class="btn btn-success icon-only"
              type="button"
              title="Raster bearbeiten"
              aria-label="Raster bearbeiten"
              :disabled="isSelectedLetterFinalized || !canEditOrDeleteAssessment(row)"
              @click="openEditAssessment(row)"
            >
              <i
                class="pi pi-pencil"
                aria-hidden="true"
              />
            </button>
            <button
              :class="['btn', 'icon-only', row.released ? 'btn-success' : 'btn-primary']"
              type="button"
              :title="row.released ? 'Freigabe zurücknehmen' : 'Freigeben'"
              :aria-label="row.released ? 'Freigabe zurücknehmen' : 'Freigeben'"
              :disabled="isSelectedLetterFinalized || !canEditOrDeleteAssessment(row)"
              @click="toggleAssessmentRelease(row)"
            >
              <i
                :class="row.released ? 'pi pi-undo' : 'pi pi-check-circle'"
                aria-hidden="true"
              />
            </button>
            <button
              class="btn danger icon-only"
              type="button"
              title="Raster löschen"
              aria-label="Raster löschen"
              :disabled="isSelectedLetterFinalized || !canEditOrDeleteAssessment(row)"
              @click="removeAssessment(row)"
            >
              <i
                class="pi pi-trash"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <div
          v-if="filteredAssessments.length === 0"
          class="empty"
        >
          Keine Daten
        </div>
      </div>

      <div class="pagination">
        <div class="pagination-left">
          <span class="meta">Gesamt: {{ assessments.total }}</span>
        </div>

        <div class="pagination-right">
          <label class="meta page-size">
            <span>Einträge:</span>
            <select
              v-model.number="assessmentsPageSize"
              class="input input-select"
              aria-label="Einträge pro Seite (Kompetenzraster)"
              @change="loadAssessments(1)"
            >
              <option
                v-for="opt in pageSizeOptions"
                :key="opt"
                :value="opt"
              >{{ opt }}
              </option>
            </select>
          </label>

          <div class="pagination-center">
            <button
              class="btn"
              :disabled="assessments.page <= 1"
              @click="loadAssessments(assessments.page - 1)"
            >
              Zurück
            </button>
            <span class="meta">Seite {{ assessments.page }} / {{ assessments.pages }}</span>
            <button
              class="btn"
              :disabled="assessments.page >= assessments.pages"
              @click="loadAssessments(assessments.page + 1)"
            >
              Weiter
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
  <!-- Dialoge (PDF, Templates, Assessments) -->
  <FeedbackFormsPrintDialog
    :open="printForms.open"
    :template="printForms.template"
    @close="closePrintForms"
  />

  <FrontSheetDialog
    :open="frontDlg.open"
    :student="frontDlg.student"
    :initial="frontDlg.initial"
    :loading="frontDlg.loading"
    @close="frontDlg.open = false"
    @save="onFrontSaved"
  />

  <BackSheetDialog
    :open="backDlg.open"
    :student="backDlg.student"
    :initial="backDlg.initial"
    @close="backDlg.open = false"
    @save="onBackSaved"
  />

  <FeedbackTemplateDialog
    :open="tplDialog.open"
    :mode="tplDialog.mode"
    :initial="tplDialog.initial"
    :allowed-subjects="subjectsSorted"
    :used-subject-ids="usedSubjectIdsInLetter"
    @close="tplDialog.open = false"
    @saved="saveTemplate"
  />

  <FeedbackAssessmentDialog
    ref="asDialogRef"
    :open="asDialog.open"
    :mode="asDialog.mode"
    :initial="asDialog.initial"
    :letter-id="selectedLetter?.id || null"
    @close="asDialog.open = false"
    @saved="saveAssessment"
  />

  <!-- Tab 1: Lernbriefe – Dialoge -->
  <FeedbackLetterDialog
    :open="lbDialog.open"
    :mode="lbDialog.mode"
    :initial="lbDialog.initial"
    @close="lbDialog.open = false"
    @saved="saveLetter"
  />

  <FeedbackLetterStudentsDialog
    :open="lbStudentsDlg.open"
    :letter-id="lbStudentsDlg.letterId"
    @close="lbStudentsDlg.open = false"
    @saved="onStudentsSaved"
  />

  <ConfirmDeleteDialog
    v-model="delLbOpen"
    :loading="delLbLoading"
    title="Lernbrief löschen?"
    message="Dieser Lernbrief wird dauerhaft gelöscht."
    :details="[
      {label: 'Titel', value: lbToDelete?.title}
    ]"
    confirm-label="Lernbrief löschen"
    @confirm="doDeleteLetter"
    @cancel="lbToDelete.value = null"
  />

  <!-- Löschen-Dialog: Template (Fach + Thema) -->
  <ConfirmDeleteDialog
    v-model="delTplOpen"
    :loading="delTplLoading"
    title="Vorlage löschen?"
    message="Dieses Vorlage wird dauerhaft gelöscht."
    :details="[
      {label: 'Fach', value: tplToDelete?.subject},
      {label: 'Thema', value: tplToDelete?.topic || tplToDelete?.title}
    ]"
    confirm-label="Vorlage löschen"
    @confirm="doDeleteTemplate"
    @cancel="tplToDelete.value = null"
  />

  <!-- Löschen-Dialog: Assessment (Thema + Schüler:in) -->
  <ConfirmDeleteDialog
    v-model="delAsOpen"
    :loading="delAsLoading"
    title="Kompetenzraster löschen?"
    message="Dieses Kompetenzraster wird dauerhaft gelöscht."
    :details="[
      {label: 'Thema', value: assessmentToDelete?.topic || assessmentToDelete?.template_title},
      {label: 'Schüler', value: assessmentToDelete?.student}
    ]"
    confirm-label="Kompetenzraster löschen"
    @confirm="doDeleteAssessment"
    @cancel="assessmentToDelete.value = null"
  />
</template>

<script setup>
import {computed, onMounted, reactive, ref, watch} from 'vue';
import {useToast} from 'primevue/usetoast';
import FeedbackTemplateDialog from './FeedbackTemplateDialog.vue';
import FeedbackAssessmentDialog from './FeedbackAssessmentDialog.vue';
import FeedbackLetterDialog from './FeedbackLetterDialog.vue';
import FeedbackLetterStudentsDialog from './dialogs/FeedbackLetterStudentsDialog.vue';
import ConfirmDeleteDialog from './dialogs/ConfirmDeleteDialog.vue';
import FeedbackFormsPrintDialog from './dialogs/FeedbackFormsPrintDialog.vue';
import FrontSheetDialog from './dialogs/FrontSheetDialog.vue';
import BackSheetDialog from './dialogs/BackSheetDialog.vue';
import {exportFeedbackLetterPdf} from '@/pdf/feedbackLetterPdf';
import {useAuthStore} from '@/stores/auth';
import api from '@/services/api';
import {getCurrentSchoolYear} from '@/api/schoolYears';
import {computeTemplatePdfFit} from '@/utils/templatePdfFit';

import {
  createTemplate,
  deleteTemplate as deleteTemplateApi,
  getTemplate,
  listTemplates,
  updateTemplate,
} from '@/api/feedbackTemplates';

import {
  createAssessment,
  deleteAssessment as deleteAssessmentApi,
  getAssessment,
  listAssessments,
  updateAssessment,
  listReleasedAssessmentsForStudent,
} from '@/api/feedbackAssessments';

import {
  createLetter,
  deleteLetter as deleteLetterApi,
  getLetter,
  getLetterDetail,
  getLetterStudents,
  listLetters,
  setLetterBackSheet,
  setLetterFrontSheet,
  updateLetter,
  finalizeLetter,
} from '@/api/feedbackLetters';

const auth = useAuthStore();
const isAdmin = computed(() => auth.isAdmin);
const isTeacher = computed(() => Number(auth.user?.roleId) === 2);
const isLetterAdminLike = computed(() => {
  if (isAdmin.value) return true;
  if (!isTeacher.value) return false;
  const perms = Array.isArray(auth.user?.permissions) ? auth.user.permissions : [];
  return perms.includes('feedback_letters_manage');
});

const canManageLetters = computed(() => isLetterAdminLike.value);
const activeTab = ref('letters'); // 'letters' | 'letterDetail' | 'templates' | 'assessments'
const selectedLetter = ref(null);

// Schülerliste pro Lernbrief (Anzeige mit Pagination)
const letterStudents = reactive({all: [], page: 1, pages: 1, total: 0});
const letterStudentsPageSize = ref(20);
const visibleLetterStudents = computed(() => {
  const all = Array.isArray(letterStudents.all) ? letterStudents.all : [];
  const sorted = [...all].sort((a, b) => {
    const af = String(a?.first_name || '');
    const bf = String(b?.first_name || '');
    const c1 = af.localeCompare(bf, 'de-DE', {sensitivity: 'base'});
    if (c1 !== 0) return c1;
    const al = String(a?.last_name || '');
    const bl = String(b?.last_name || '');
    return al.localeCompare(bl, 'de-DE', {sensitivity: 'base'});
  });
  const size = Number(letterStudentsPageSize.value) || 20;
  const page = Number(letterStudents.page) || 1;
  const start = (page - 1) * size;
  return sorted.slice(start, start + size);
});

const templateSearchDebounceMs = 250;
const templateSearchTimer = ref(null);

function onTemplateTopicInput() {
  if (templateSearchTimer.value) {
    window.clearTimeout(templateSearchTimer.value);
  }
  templateSearchTimer.value = window.setTimeout(() => {
    loadTemplates(1);
  }, templateSearchDebounceMs);
}

const assessmentSearchDebounceMs = 250;
const assessmentSearchTimer = ref(null);

function onAssessmentTopicInput() {
  if (assessmentSearchTimer.value) {
    window.clearTimeout(assessmentSearchTimer.value);
  }
  assessmentSearchTimer.value = window.setTimeout(() => {
    loadAssessments(1);
  }, assessmentSearchDebounceMs);
}

function displayStudentName(s) {
  const override = String(s?.student_name_override ?? '').trim();
  if (override) return override;

  const first = String(s?.first_name ?? '').trim();
  const last = String(s?.last_name ?? '').trim();
  return `${first} ${last}`.trim();
}

async function loadLetterStudents(page = 1) {
  if (!selectedLetter.value || !selectedLetter.value.id) {
    Object.assign(letterStudents, {all: [], page: 1, pages: 1, total: 0});
    return;
  }
  try {
    const res = await getLetterStudents(Number(selectedLetter.value.id));
    const items = Array.isArray(res?.items) ? res.items : [];
    letterStudents.all = items;
    letterStudents.total = items.length;
    letterStudents.pages = Math.max(1, Math.ceil(items.length / (Number(letterStudentsPageSize.value) || 20)));
    letterStudents.page = Math.min(Math.max(1, Number(page) || 1), letterStudents.pages);
  } catch (e) {
    Object.assign(letterStudents, {all: [], page: 1, pages: 1, total: 0});
    console.error('Letter-Students laden fehlgeschlagen', e);
  }
}

// Schüler-Zuweisungen: Count je Lernbrief
const letterAssignedCounts = reactive(new Map());
// Fächer-Zuweisungen: Count je Lernbrief
const letterSubjectCounts = reactive(new Map());
// Dialog: Schüler zuweisen
const lbStudentsDlg = reactive({open: false, letterId: null});

const selectedHasStudents = computed(() => {
  if (!selectedLetter.value) return false;
  const id = Number(selectedLetter.value.id);
  if (!Number.isFinite(id)) return false;
  const cnt = letterAssignedCounts.get(id);
  return Number.isFinite(cnt) ? cnt > 0 : false;
});

// --- Tab 1: Lernbriefe
const letters = reactive({items: [], page: 1, pages: 1, total: 0});
const letterFilters = reactive({q: ''});
const lettersPageSize = ref(20);

const filteredLetters = computed(() => {
  const items = letters.items || [];
  const q = (letterFilters.q || '').trim().toLowerCase();
  if (!q) return items;
  return items.filter(r => String(r?.title ?? '').toLowerCase().includes(q));
});

// Dialoge/Status: Lernbriefe
const lbDialog = reactive({open: false, mode: 'new', initial: null});
const delLbOpen = ref(false);
const delLbLoading = ref(false);
const lbToDelete = ref(null);

function openAssignStudents(row) {
  if (!row?.id) return;
  lbStudentsDlg.letterId = Number(row.id);
  lbStudentsDlg.open = true;
}

function onStudentsSaved({letterId, count}) {
  letterAssignedCounts.set(Number(letterId), Number(count) || 0);
  lbStudentsDlg.open = false;

  // Wenn der aktuell ausgewählte Lernbrief betroffen ist: neu laden wie "Öffnen",
  // aber Tab nicht erzwingen.
  if (selectedLetter.value && Number(selectedLetter.value.id) === Number(letterId)) {
    openLetterById(Number(letterId), {switchToDetailTab: false});
  }

  // Falls der aktuell ausgewählte Lernbrief betroffen ist und nun keine Schüler mehr hat → Tabs schließen
  if (selectedLetter.value && Number(selectedLetter.value.id) === Number(letterId) && count <= 0) {
    activeTab.value = 'letters';
  }
  // Andernfalls: wenn der Lernbrief-Tab offen ist, Liste aktualisieren
  if (selectedLetter.value && Number(selectedLetter.value.id) === Number(letterId) && count > 0 && activeTab.value === 'letterDetail') {
    loadLetterStudents(letterStudents.page || 1);
  }
}

// --- Bestehende: Templates & Assessments
const templates = reactive({items: [], page: 1, pages: 1, total: 0});
const assessments = reactive({items: [], page: 1, pages: 1, total: 0});

// Vorlagen- und Kompetenzraster-Filter trennen (damit die Tabellen unabhängig sind)
const templateFilters = reactive({scope: 'letter', topic: '', teacherId: null, subjectId: null});
const assessmentFilters = reactive({topic: '', teacherId: null, subjectId: null, studentId: null});

// Options (ID-basiert)
const templateTeacherOptions = ref([]);
const templateSubjectOptions = ref([]);

const assessmentTeacherOptions = ref([]);
const assessmentSubjectOptions = ref([]);
const assessmentStudentOptions = ref([]);

const selected = ref([]);
const teacherOptions = ref([]);
const subjectOptions = ref([]);
const studentOptions = ref([]);
const printForms = reactive({open: false, template: null});

// Dialog: Vorblatt bearbeiten
const frontDlg = reactive({
  open: false,
  student: null,
  initial: null,
  loading: false
});

// Dialog: Rückblatt bearbeiten
const backDlg = reactive({
  open: false,
  student: null,
  initial: null
});

const tplDialog = reactive({open: false, mode: 'new', initial: null});
const asDialog = reactive({open: false, mode: 'new', initial: null});
const asDialogRef = ref(null);

// Loading-State für „autom. Raster erzeugen“ (hält aktuell verarbeitete Template-ID)
const autoCreateTplId = ref(null);

// Delete-Dialog-Status (bestehend)
const delTplOpen = ref(false);
const delTplLoading = ref(false);
const tplToDelete = ref(null);

const delAsOpen = ref(false);
const delAsLoading = ref(false);
const assessmentToDelete = ref(null);

const pageSizeOptions = [10, 20, 50, 100];
const templatesPageSize = ref(20);
const assessmentsPageSize = ref(20);

const filteredTemplates = computed(() => {
  const items = templates.items || [];
  const scope = (templateFilters.scope || 'letter').trim();
  const topic = (templateFilters.topic || '').trim().toLowerCase();
  const teacher = (templateFilters.teacher || '').trim();
  const subject = (templateFilters.subject || '').trim();

  let pool = items;

  // Scope-Filter: nur Vorlagen dieses Lernbriefs
  if (scope === 'letter' && selectedLetter.value) {
    const currentLetterId = Number(selectedLetter.value.id) || 0;
    const allowedIds = new Set(
        (Array.isArray(letterTemplatesBySubject.value) ? letterTemplatesBySubject.value : [])
            .map(x => Number(x?.template_id))
            .filter(n => Number.isFinite(n) && n > 0)
    );
    pool = pool.filter(r => {
      const tid = Number(r?.id);
      const lid = Number(r?.feedback_letter_id);
      return (Number.isFinite(tid) && allowedIds.has(tid)) ||
          (Number.isFinite(lid) && lid === currentLetterId);
    });
  }

  return pool.filter(r => {
    const topicStr = String(r?.topic ?? '').toLowerCase();
    const noteStr = String(r?.internal_note ?? '').toLowerCase();
    const okTopic = topic ? (topicStr.includes(topic) || noteStr.includes(topic)) : true;
    const okTeacher = teacher ? String(r?.teacher ?? '') === teacher : true;
    const okSubject = subject ? String(r?.subject ?? '') === subject : true;
    return okTopic && okTeacher && okSubject;
  });
});

const filteredAssessments = computed(() => {
  const items = assessments.items || [];
  const topic = (assessmentFilters.topic || '').trim().toLowerCase();
  const teacher = (assessmentFilters.teacher || '').trim();
  const subject = (assessmentFilters.subject || '').trim();
  const student = (assessmentFilters.student || '').trim();
  return items.filter(r => {
    const topicStr = String(r?.topic ?? '').toLowerCase();
    const noteStr = String(r?.internal_note ?? '').toLowerCase();
    const okTopic = topic ? (topicStr.includes(topic) || noteStr.includes(topic)) : true;
    const okTeacher = teacher ? String(r?.teacher ?? '') === teacher : true;
    const okSubject = subject ? String(r?.subject ?? '') === subject : true;
    const okStudent = student ? String(r?.student ?? '') === student : true;
    return okTopic && okTeacher && okSubject && okStudent;
  });
});
const toast = useToast();

function hasLinkedAssessments(templateOrId) {
  if (templateOrId && typeof templateOrId === 'object') {
    return Boolean(templateOrId?.has_assessments ?? templateOrId?.hasAssessments);
  }
  const tid = Number(templateOrId);
  if (!Number.isFinite(tid) || tid <= 0) return false;
  const items = Array.isArray(assessments.items) ? assessments.items : [];
  return items.some(a => {
    const atid = a?.template_id ?? a?.templateId ?? null;
    const n = Number(atid);
    return Number.isFinite(n) && n === tid;
  });
}

// Admin oder Ersteller (user_id) dürfen bearbeiten/löschen
function canEditOrDeleteTemplate(row) {
  const creatorId = Number(row?.user_id);
  const me = Number(auth.user?.id);
  if (isLetterAdminLike.value) return true;
  return Number.isFinite(creatorId) && Number.isFinite(me) && creatorId > 0 && creatorId === me;
}

// Nur wenn die Vorlage mit dem aktuell geöffneten Lernbrief verlinkt ist
function isTemplateLinkedToSelectedLetter(row) {
  const currentLetterId = Number(selectedLetter.value?.id) || 0;
  if (currentLetterId <= 0) return false;
  const rowLetterId = Number(row?.feedback_letter_id) || 0;
  if (rowLetterId === currentLetterId) return true;
  // Zusätzlich: Mapping aus templates_by_subject berücksichtigen
  const linkedIds = templateIdsInSelectedLetter.value; // Set<number>
  return linkedIds.has(Number(row?.id));
}

// Kombiniert: Berechtigung + Link zum aktuellen Lernbrief
function canEditTemplateInSelectedLetter(row) {
  return isTemplateLinkedToSelectedLetter(row) && canEditOrDeleteTemplate(row);
}

function canAutoCreateForTemplate(row) {
  return isTemplateLinkedToSelectedLetter(row) && canEditOrDeleteTemplate(row);
}

// Assessments: gleiche Berechtigungslogik
function canEditOrDeleteAssessment(row) {
  const creatorId = Number(row?.teacher_id);
  const me = Number(auth.user?.id);

  if (isLetterAdminLike.value) return true;

  return Number.isFinite(creatorId) && Number.isFinite(me) && creatorId > 0 && creatorId === me;
}

async function toggleAssessmentRelease(row) {
  const id = Number(row?.id);
  if (!Number.isFinite(id) || id <= 0) return;

  try {
    // Volldatensatz holen (inkl. assessment_json)
    const full = await getAssessment(id);
    const tplId = Number(full?.template_id ?? row?.template_id) || 0;
    const studentId = Number(full?.student_id ?? row?.student_id) || 0;
    const assessedOn = (full?.assessed_on ?? full?.date ?? row?.date) || new Date().toISOString().slice(0, 10);

    let json = full?.assessment_json;
    if (typeof json === 'string') {
      try {
        json = JSON.parse(json);
      } catch {
        json = {};
      }
    }
    json = (json && typeof json === 'object') ? json : {};

    const currentReleased = !!(row?.released ?? json?.released);
    const nextReleased = !currentReleased;

    const payload = {
      template_id: tplId,
      student_id: studentId,
      assessed_on: assessedOn,
      assessment_json: {
        ...json,
        released: nextReleased,
      },
    };

    await updateAssessment(id, payload);
    await loadAssessments(assessments.page);
    toast.add({severity: 'success', summary: nextReleased ? 'Freigegeben.' : 'Freigabe zurückgenommen.', life: 3000});
  } catch (e) {
    console.error('Release umschalten fehlgeschlagen', e.response.data.error.message);
    toast.add({
      severity: 'error',
      summary: e.response.data.error.message ? e.response.data.error.message : 'Freigabe konnte nicht geändert werden.',
      life: 4000
    });
  }
}

function toLocal(val) {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

// --- Letters API
async function loadLetters(page = 1) {
  const res = await listLetters({
    page,
    limit: lettersPageSize.value,
    q: letterFilters.q
  });
  Object.assign(letters, res);

  // NEU: Counts/Flags aus der Liste übernehmen (kein Einzel-Nachladen mehr)
  try {
    const items = Array.isArray(res?.items) ? res.items : [];
    for (const it of items) {
      const id = Number(it?.id);
      if (!Number.isFinite(id) || id <= 0) continue;

      const hasStudents = Boolean(it?.has_students ?? it?.hasStudents ?? false);
      const hasSubjects = Boolean(it?.has_subjects ?? it?.hasSubjects ?? false);

      letterAssignedCounts.set(id, hasStudents ? 1 : 0);
      letterSubjectCounts.set(id, hasSubjects ? 1 : 0);
    }
  } catch {
    // defensiv: UI funktioniert auch ohne Maps, aber Tabs/Checks könnten konservativ ausfallen
  }
}

function resetLetterFilters() {
  letterFilters.q = '';
  loadLetters(1);
}

function openNewLetter() {
  lbDialog.mode = 'new';
  lbDialog.initial = null;
  lbDialog.open = true;
}

async function openEditLetter(row) {
  lbDialog.mode = 'edit';
  try {
    const data = await getLetter(row.id);
    // Detail-Endpoint liefert Metadaten unter data.letter
    const base = data?.letter ?? data ?? row;
    const subs = Array.isArray(data?.subjects)
        ? [...data.subjects].sort((a, b) => (Number(a.sort ?? 0) - Number(b.sort ?? 0))).map(s => s.id)
        : [];
    lbDialog.initial = {...base, subject_ids: subs, subjects: data?.subjects || []};
    lbDialog.open = true;
  } catch (e) {
    console.error('Lernbrief laden fehlgeschlagen', e);
    // Fallback: öffne mit vorhandenen Row-Daten
    lbDialog.initial = row;
    lbDialog.open = true;
  }
}

async function saveLetter({mode, id, payload}) {
  try {
    const prevTab = activeTab.value;

    if (mode === 'edit') {
      await updateLetter(id, payload);
    } else {
      await createLetter(payload);
    }

    lbDialog.open = false;

    // Liste aktualisieren
    await loadLetters(letters.page);

    // Wenn der aktuell ausgewählte Lernbrief bearbeitet wurde: neu laden wie "Öffnen"
    if (mode === 'edit' && selectedLetter.value && Number(selectedLetter.value.id) === Number(id)) {
      await openLetterById(Number(id), {switchToDetailTab: false});
    }

    activeTab.value = prevTab;
    toast.add({severity: 'success', summary: 'Gespeichert.', life: 3500});
  } catch (e) {
    const msg = e?.response?.data?.error?.message
        || e?.response?.data?.message
        || e?.message
        || 'Speichern fehlgeschlagen.';
    toast.add({severity: 'error', summary: msg, life: 4500});
    console.error('Lernbrief speichern fehlgeschlagen', e);
  }
}

const letterTemplatesBySubject = ref([]);
// Kompakte Assessments (nur Status), geliefert vom konsolidierten Detail-Endpoint für Tab 2
const letterAssessmentStatuses = ref([]);

/**
 * Öffnet/Lädt einen Lernbrief wie der "Öffnen"-Button, aber anhand der ID.
 * Holt dazu eine passende row aus der Liste (Fallback: selectedLetter / Minimrow).
 */
function getLetterRowById(letterId) {
  const lid = Number(letterId);
  if (!Number.isFinite(lid) || lid <= 0) return null;

  const fromList = (Array.isArray(letters.items) ? letters.items : [])
      .find(r => Number(r?.id) === lid);
  if (fromList) return fromList;

  if (selectedLetter.value && Number(selectedLetter.value?.id) === lid) {
    return selectedLetter.value;
  }

  return {id: lid};
}

async function openLetterById(letterId, {switchToDetailTab = false} = {}) {
  const row = getLetterRowById(letterId);
  if (!row) return;
  await openLetter(row, {switchToDetailTab});
}

// NEU: Guard gegen paralleles/doppeltes Laden beim Öffnen
const openingLetterId = ref(null);

async function openLetter(row, {switchToDetailTab = true} = {}) {
  const prevTab = activeTab.value;

  const requestedId = Number(row?.id) || 0;
  if (!Number.isFinite(requestedId) || requestedId <= 0) return;

  // Wenn gerade derselbe Lernbrief geöffnet wird: ignorieren (Doppelklick)
  if (openingLetterId.value === requestedId) return;
  openingLetterId.value = requestedId;

  try {
    // WICHTIG: erst selectedLetter setzen, dann Filter resetten (ohne Requests!)
    selectedLetter.value = row || null;

    resetTemplateFilters({reload: false});
    resetAssessmentFilters({reload: false});

    // 1) Detaildaten exakt für die angeforderte ID laden
    await enterLetterDetailTab();

    // 2) Tabwechsel erst danach
    if (switchToDetailTab) {
      prefetchedForDetail.value = true;
      activeTab.value = 'letterDetail';
    } else {
      activeTab.value = prevTab;
    }

    // 3) Jetzt genau einmal Templates/Assessments für diesen Lernbrief laden
    templateFilters.scope = 'letter';
    await Promise.allSettled([
      loadTemplates(1),
      loadAssessments(1),
    ]);
  } finally {
    openingLetterId.value = null;
  }
}

function removeLetter(rowOrId) {
  const row = (typeof rowOrId === 'object')
      ? rowOrId
      : (letters.items || []).find(r => r.id === rowOrId);

  lbToDelete.value = row ?? {id: rowOrId};
  delLbOpen.value = true;
}

async function doDeleteLetter() {
  const id = lbToDelete.value?.id;
  if (!id) {
    delLbOpen.value = false;
    return;
  }
  delLbLoading.value = true;
  try {
    await deleteLetterApi(id);
    await loadLetters(letters.page);
    toast.add({severity: 'success', summary: 'Gelöscht.', life: 3500});
    if (selectedLetter.value && selectedLetter.value.id === id) {
      selectedLetter.value = null;
      if (activeTab.value === 'letterDetail') activeTab.value = 'letters';
    }
  } catch (e) {
    const msg = e?.response?.data?.error?.message
        || e?.response?.data?.message
        || e?.message
        || 'Löschen fehlgeschlagen.';
    toast.add({severity: 'error', summary: msg, life: 4500});
    console.error('Lernbrief löschen fehlgeschlagen', e);
  } finally {
    delLbLoading.value = false;
    delLbOpen.value = false;
    lbToDelete.value = null;
  }
}

// --- Bestehend: Templates / Assessments (unverändert)
function updateFilterOptions() {
  const tSet = new Set();
  const sSet = new Set();
  const stSet = new Set();

  // templates.items.forEach(r => {
  //   if (r?.teacher) tSet.add(r.teacher);
  //   if (r?.subject) sSet.add(r.subject);
  // });

  assessments.items.forEach(r => {
    if (r?.teacher) tSet.add(r.teacher);
    if (r?.subject) sSet.add(r.subject);
    if (r?.student) stSet.add(r.student);
  });

  teacherOptions.value = Array.from(tSet).sort((a, b) => String(a).localeCompare(String(b), 'de-DE'));
  subjectOptions.value = Array.from(sSet).sort((a, b) => String(a).localeCompare(String(b), 'de-DE'));
  studentOptions.value = Array.from(stSet).sort((a, b) => String(a).localeCompare(String(b), 'de-DE'));
}

async function loadTemplateFilterOptions() {
  const tSet = new Set();
  const sSet = new Set();

  // Wir laden alle Templates OHNE teacher/subject-Filter und sammeln daraus die Options.
  // Backend limitiert auf max 100 → daher paginieren, aber mit Sicherheitslimit.
  const hardPageLimit = 25; // maximal 2500 Items scannen (25 * 100)
  let page = 1;

  while (page <= hardPageLimit) {
    const res = await listTemplates({
      page,
      limit: 100,
      teacher: '',
      subject: '',
    });

    const items = Array.isArray(res?.items) ? res.items : [];
    for (const r of items) {
      if (r?.teacher) tSet.add(r.teacher);
      if (r?.subject) sSet.add(r.subject);
    }

    const total = Number(res?.total) || 0;
    const pages = Math.max(1, Math.ceil(total / 100));
    if (page >= pages) break;
    page += 1;
  }

  // Falls aktuell ein Filter gesetzt ist, aber der Name aus irgendeinem Grund nicht in den 1..N Seiten vorkam,
  // behalten wir ihn trotzdem im Dropdown, damit die Auswahl nicht "verschwindet".
  const currentTeacher = (templateFilters.teacher || '').trim();
  const currentSubject = (templateFilters.subject || '').trim();
  if (currentTeacher) tSet.add(currentTeacher);
  if (currentSubject) sSet.add(currentSubject);

  teacherOptions.value = Array.from(tSet).sort((a, b) => String(a).localeCompare(String(b), 'de-DE'));
  subjectOptions.value = Array.from(sSet).sort((a, b) => String(a).localeCompare(String(b), 'de-DE'));
}

async function loadTemplates(page = 1) {
  const res = await listTemplates({
    page,
    limit: templatesPageSize.value,
    topic: templateFilters.topic,
    teacherId: templateFilters.teacherId,
    subjectId: templateFilters.subjectId,
    scope: templateFilters.scope,
    feedbackLetterId: (templateFilters.scope === 'letter')
        ? (Number(selectedLetter.value?.id) || null)
        : null
  });

  Object.assign(templates, res);

  const optTeachers = Array.isArray(res?.options?.teachers) ? res.options.teachers : [];
  const optSubjects = Array.isArray(res?.options?.subjects) ? res.options.subjects : [];

  templateTeacherOptions.value = optTeachers
      .filter(x => Number.isFinite(Number(x?.id)) && String(x?.label ?? '').trim() !== '')
      .map(x => ({id: Number(x.id), label: String(x.label)}));

  templateSubjectOptions.value = optSubjects
      .filter(x => Number.isFinite(Number(x?.id)) && String(x?.label ?? '').trim() !== '')
      .map(x => ({id: Number(x.id), label: String(x.label)}));

  // Nach jedem Laden der Liste Überlänge pro Zeile berechnen und Badge-Map setzen
  await updateTemplateOverlengthFlags();
}

async function loadAssessments(page = 1) {
  const res = await listAssessments({
    page,
    limit: assessmentsPageSize.value,
    topic: assessmentFilters.topic,
    teacherId: assessmentFilters.teacherId,
    subjectId: assessmentFilters.subjectId,
    studentId: assessmentFilters.studentId,
    feedbackLetterId: Number(selectedLetter.value?.id) || null
  });

  Object.assign(assessments, res);

  const optSubjects = Array.isArray(res?.options?.subjects) ? res.options.subjects : [];
  const optTeachers = Array.isArray(res?.options?.teachers) ? res.options.teachers : [];
  const optStudents = Array.isArray(res?.options?.students) ? res.options.students : [];

  assessmentSubjectOptions.value = optSubjects
      .filter(x => Number.isFinite(Number(x?.id)) && String(x?.label ?? '').trim() !== '')
      .map(x => ({id: Number(x.id), label: String(x.label)}));

  assessmentTeacherOptions.value = optTeachers
      .filter(x => Number.isFinite(Number(x?.id)) && String(x?.label ?? '').trim() !== '')
      .map(x => ({id: Number(x.id), label: String(x.label)}));

  assessmentStudentOptions.value = optStudents
      .filter(x => Number.isFinite(Number(x?.id)) && String(x?.label ?? '').trim() !== '')
      .map(x => ({id: Number(x.id), label: String(x.label)}));
}

async function reloadAll(page = 1) {
  selected.value = [];
  await Promise.all([loadTemplates(page), loadAssessments(page), loadLetters(page)]);
  updateFilterOptions();
  await loadTemplateFilterOptions();
}

function resetTemplateFilters({reload = true} = {}) {
  templateFilters.scope = 'letter';
  templateFilters.topic = '';
  templateFilters.teacherId = null;
  templateFilters.subjectId = null;

  if (reload) {
    loadTemplates(1);
  }
}

function resetAssessmentFilters({reload = true} = {}) {
  assessmentFilters.topic = '';
  assessmentFilters.teacherId = null;
  assessmentFilters.subjectId = null;
  assessmentFilters.studentId = null;
  selected.value = [];

  if (reload) {
    loadAssessments(1);
  }
}

// --- Bestehend: Templates/Assessments Handlers ...
async function openEditTemplate(row) {
  tplDialog.mode = 'edit';
  try {
    tplDialog.initial = await getTemplate(row.id);
    tplDialog.open = true;
  } catch (e) {
    console.error('Vorlage laden fehlgeschlagen', e);
    tplDialog.initial = row;
    tplDialog.open = true;
  }
}

function openNewTemplate() {
  tplDialog.mode = 'new';
  // Aktuellen Lernbrief an den Dialog übergeben, damit feedback_letter_id gespeichert wird
  tplDialog.initial = {
    feedback_letter_id: selectedLetter.value?.id ?? null
  };
  tplDialog.open = true;
}

async function openPrintForms(row) {
  const tpl = row || {};
  const templateLetterId = Number(tpl.feedback_letter_id) || 0;
  const selectedLetterId = Number(selectedLetter.value?.id) || 0;

  let students = [];
  try {
    if (templateLetterId === selectedLetterId) {
      const res = await getLetterStudents(templateLetterId);
      students = Array.isArray(res?.items) ? res.items : [];
    }
  } catch (e) {
    students = [];
    console.warn('Schüler für PDF-Vorauswahl konnten nicht geladen werden', e);
  }

  const preselectedStudentIds = students
      .map(s => Number(s?.id))
      .filter(n => Number.isFinite(n) && n > 0);

  printForms.template = {
    ...tpl,
    students,
    preselected_student_ids: preselectedStudentIds,
  };
  printForms.open = true;
}

function closePrintForms() {
  printForms.open = false;
  printForms.template = null;
}

async function autoCreateAssessmentsForTemplate(row) {
  // Bestimme Lernbrief-ID
  const letterId = Number(selectedLetter.value?.id ?? row?.feedback_letter_id) || 0;
  const templateId = Number(row?.id) || 0;
  const me = Number(auth.user?.id) || 0;

  if (letterId <= 0 || templateId <= 0 || me <= 0) {
    toast.add({severity: 'error', summary: 'Keine gültige Vorlage oder Lernbrief gewählt.', life: 3500});
    return;
  }

  autoCreateTplId.value = templateId;
  try {
    // Schüler des Lernbriefs laden
    const ls = await getLetterStudents(letterId);
    const letterStudentsArr = Array.isArray(ls?.items) ? ls.items : [];
    const studentIdsInLetter = letterStudentsArr
        .map(s => Number(s?.id ?? s?.student_id))
        .filter(n => Number.isFinite(n) && n > 0);

    // Bereits existierende Assessments für diesen Lernbrief einsammeln (paging)
    const usedStudentIds = new Set();
    let page = 1;
    let pages = 1;
    do {
      const aRes = await listAssessments({page, limit: 100, feedbackLetterId: letterId});
      const aItems = Array.isArray(aRes?.items) ? aRes.items : [];
      aItems.forEach(a => {
        const tId = Number(a?.template_id ?? a?.templateId) || 0;
        const sId = Number(a?.student_id ?? a?.studentId) || 0;
        if (tId === templateId && sId > 0) usedStudentIds.add(sId);
      });
      pages = Math.max(1, Number(aRes?.pages) || 1);
      page += 1;
    } while (page <= pages);

    // Offene Schüler bestimmen
    const openStudentIds = studentIdsInLetter.filter(id => !usedStudentIds.has(id));
    if (openStudentIds.length === 0) {
      toast.add({severity: 'info', summary: 'Keine offenen Schüler – es wurden keine Raster erzeugt.', life: 3500});
      return;
    }

    // Template-Details (inkl. template_json) laden
    const fullTpl = await getTemplate(templateId);
    const tplSnapshot = (fullTpl && typeof fullTpl === 'object') ? (fullTpl.template_json ?? {}) : {};

    const today = new Date().toISOString().slice(0, 10);

    // Assessments erzeugen (parallel)
    await Promise.all(
        openStudentIds.map(studentId => createAssessment({
          user_id: me,
          template_id: templateId,
          student_id: studentId,
          assessed_on: today,
          assessment_json: {
            template_snapshot: tplSnapshot,
            values: {},
          },
        }))
    );

    // Listen aktualisieren (Kompetenzraster + Vorlagen) und Erfolgsmeldung
    await Promise.all([
      loadAssessments(assessments.page || 1),
      loadTemplates(templates.page || 1),
    ]);
    toast.add({severity: 'success', summary: `Erstellt: ${openStudentIds.length} Raster.`, life: 3500});
  } catch (e) {
    console.error('Autom. Raster erzeugen fehlgeschlagen', e);
    toast.add({severity: 'error', summary: 'Automatisches Erzeugen fehlgeschlagen.', life: 4000});
  } finally {
    autoCreateTplId.value = null;
  }
}

function openNewAssessment() {
  asDialog.mode = 'new';
  asDialog.initial = null;
  asDialog.open = true;
}

async function openEditAssessment(row) {
  asDialog.mode = 'edit';
  try {
    asDialog.initial = await getAssessment(row.id);
    asDialog.open = true;
  } catch (e) {
    console.error('Kompetenzraster laden fehlgeschlagen', e);
    asDialog.initial = row;
    asDialog.open = true;
  }
}

const templateOverlengthMap = reactive(new Map());

async function updateTemplateOverlengthFlags() {
  try {
    const items = Array.isArray(templates.items) ? templates.items : [];
    // Map leeren (reaktiv)
    if (typeof templateOverlengthMap.clear === 'function') {
      templateOverlengthMap.clear();
    } else {
      // Fallback: einzeln löschen
      Array.from(templateOverlengthMap.keys()).forEach(k => templateOverlengthMap.delete(k));
    }

    await Promise.all(items.map(async (row) => {
      const id = Number(row?.id) || 0;
      if (!Number.isFinite(id) || id <= 0) return;

      let tplJson = row?.template_json;
      if (typeof tplJson === 'string') {
        try {
          tplJson = JSON.parse(tplJson);
        } catch {
          tplJson = null;
        }
      }

      if (!tplJson || typeof tplJson !== 'object') {
        // Detail nachladen, um template_json zu erhalten
        try {
          const full = await getTemplate(id);
          tplJson = full?.template_json ?? null;
        } catch {
          tplJson = null;
        }
      }

      const subjectTitle = String(row?.subject || '').trim();
      const topic = String(row?.topic || '').trim();

      const info = computeTemplatePdfFit({
        templateJson: tplJson,
        subjectTitle,
        topic
      });

      templateOverlengthMap.set(id, Boolean(info?.isOverLimit));
    }));
  } catch (e) {
    console.warn('Überlänge-Status konnte nicht vollständig berechnet werden', e);
  }
}

async function saveTemplate({mode, id, payload, pdfIsOverLimit}) {
  try {
    let saved = null;

    if (mode === 'edit') {
      saved = await updateTemplate(id, payload);
    } else {
      saved = await createTemplate(payload);
    }

    // Badge-Status lokal merken (für Tabellenanzeige)
    const savedId = Number(saved?.id ?? id) || 0;
    if (savedId > 0 && typeof pdfIsOverLimit === 'boolean') {
      templateOverlengthMap.set(savedId, pdfIsOverLimit);
    }

    // Erfolg: Dialog schließen
    tplDialog.open = false;

    // Tabelle mit aktuellen Filtern neu laden
    await Promise.allSettled([
      loadTemplates(templates.page),
      loadAssessments(assessments.page),
    ]);

    // Nach Speichern die Zuordnungsliste des aktuell geöffneten Lernbriefs aktualisieren
    try {
      const lid = Number(selectedLetter.value?.id) || 0;
      if (lid > 0) {
        const data = await getLetter(lid);
        letterTemplatesBySubject.value = Array.isArray(data?.templates_by_subject) ? data.templates_by_subject : [];
      }
    } catch (e) {
      console.warn('Aktualisierung der Vorlagen-Zuordnung nach Speichern fehlgeschlagen', e);
    }

    toast.add({severity: 'success', summary: 'Gespeichert.', life: 3500});
  } catch (e) {
    const status = e?.response?.status;
    const code = e?.response?.data?.error?.code;
    const msg = e?.response?.data?.error?.message || e?.response?.data?.message || e?.message;

    if (status === 403 && code === 'PERMISSION_DENIED') {
      toast.add({severity: 'warn', summary: 'Dieses Fach ist Ihnen nicht zugeordnet.', life: 4500});
    } else if (status === 409 && code === 'CONFLICT') {
      toast.add({
        severity: 'warn',
        summary: 'Für dieses Fach existiert in diesem Lernbrief bereits eine Vorlage.',
        life: 4500
      });
    } else if (status === 422 && code === 'VALIDATION_ERROR') {
      toast.add({severity: 'error', summary: 'Eingaben ungültig. Bitte prüfen.', detail: msg, life: 4500});
    } else {
      toast.add({severity: 'error', summary: 'Speichern fehlgeschlagen.', detail: msg, life: 4500});
    }
    // Wichtig: Dialog offen lassen, damit die Auswahl korrigiert werden kann.
  }
}

async function saveAssessment({mode, id, payload}) {
  try {
    if (mode === 'edit') {
      await updateAssessment(id, payload);
    } else {
      await createAssessment(payload);
    }
    asDialog.open = false;

    await loadAssessments(assessments.page);
    await loadTemplates(templates.page);

    toast.add({severity: 'success', summary: 'Gespeichert.', life: 3500});
  } catch (e) {
    const status = e?.response?.status;
    const code = e?.response?.data?.error?.code;
    if (status === 422 && code === 'VALIDATION_ERROR') {
      const fields = e?.response?.data?.error?.details?.fields || {};
      asDialogRef.value?.applyServerValidationErrors?.(fields);
    } else {
      console.error('Speichern fehlgeschlagen', e);
    }
  }
}

async function onDuplicateTemplate(id) {
  tplDialog.mode = 'duplicate';
  try {
    const base = await getTemplate(id);
    tplDialog.initial = {
      ...base,
      feedback_letter_id: selectedLetter.value?.id ?? null
    };
    tplDialog.open = true;
  } catch (e) {
    console.error('Vorlage laden fehlgeschlagen (Duplizieren)', e);
    alert('Vorlage konnte nicht geladen werden.');
  }
}

async function removeTemplate(rowOrId) {
  const row = (typeof rowOrId === 'object')
      ? rowOrId
      : (templates.items || []).find(r => r.id === rowOrId);
  const templateId = row?.id ?? rowOrId;

  if (hasLinkedAssessments(row)) {
    alert('Kann nicht gelöscht werden: verknüpfte Kompetenzraster vorhanden');
    return;
  }

  tplToDelete.value = row ?? {id: templateId};
  delTplOpen.value = true;
}


async function removeAssessment(rowOrId) {
  const row = (typeof rowOrId === 'object')
      ? rowOrId
      : (assessments.items || []).find(r => r.id === rowOrId);

  assessmentToDelete.value = row ?? {id: rowOrId};
  delAsOpen.value = true;
}

async function doDeleteTemplate() {
  const id = tplToDelete.value?.id;
  if (!id) {
    delTplOpen.value = false;
    return;
  }
  delTplLoading.value = true;
  try {
    await deleteTemplateApi(id);
    await loadTemplates(templates.page);

    // WICHTIG: Nach dem Löschen ggf. belegte Fächer aktualisieren,
    // damit im Dialog freie Fächer sofort auswählbar sind.
    try {
      const lid = Number(selectedLetter.value?.id) || 0;
      if (lid > 0) {
        const data = await getLetter(lid);
        const tbs = Array.isArray(data?.templates_by_subject) ? data.templates_by_subject : [];
        letterTemplatesBySubject.value = tbs;
      }
    } catch (e) {
      // still: Dialog bleibt nutzbar; Liste wird beim nächsten Öffnen sicher aktualisiert
      console.warn('Vorlagen-Zuordnung nach Löschen nicht aktualisierbar', e);
    }

    toast.add({severity: 'success', summary: 'Gelöscht.', life: 3500});
  } catch (e) {
    console.error('Vorlage löschen fehlgeschlagen', e);
  } finally {
    delTplLoading.value = false;
    delTplOpen.value = false;
    tplToDelete.value = null;
  }
}

async function doDeleteAssessment() {
  const id = assessmentToDelete.value?.id;
  if (!id) {
    delAsOpen.value = false;
    return;
  }
  delAsLoading.value = true;
  try {
    await deleteAssessmentApi(id);

    await loadAssessments(assessments.page);
    await loadTemplates(templates.page);

    toast.add({severity: 'success', summary: 'Gelöscht.', life: 3500});
  } catch (e) {
    console.error('Kompetenzraster löschen fehlgeschlagen', e);
  } finally {
    delAsLoading.value = false;
    delAsOpen.value = false;
    assessmentToDelete.value = null;
  }
}

const templateIdsInSelectedLetter = computed(() => {
  const tbs = Array.isArray(letterTemplatesBySubject.value) ? letterTemplatesBySubject.value : [];
  const ids = tbs
      .map(x => Number(x?.template_id))
      .filter(n => Number.isFinite(n) && n > 0);
  return new Set(ids);
});

// Fächer des ausgewählten Lernbriefs (sortiert) + Abkürzungen + Template-Mapping
const letterSubjects = ref([]);
const subjectsSorted = computed(() => Array.isArray(letterSubjects.value) ? letterSubjects.value : []);

const subjectAbbrMap = computed(() => {
  const m = new Map();
  subjectsSorted.value.forEach(s => {
    const id = Number(s?.id);
    if (!Number.isFinite(id)) return;
    const abbr = String(s?.abbreviation || (s?.title ? String(s.title).slice(0, 2) : '')).toUpperCase();
    m.set(id, abbr);
  });
  return m;
});

// NEU: templateIdsBySubjectMap (subject_id -> Set<template_id>) – mehrere Vorlagen pro Fach
const templateIdsBySubjectMap = computed(() => {
  const tbs = Array.isArray(letterTemplatesBySubject.value) ? letterTemplatesBySubject.value : [];
  const m = new Map();

  tbs.forEach(x => {
    const sid = Number(x?.subject_id);
    const tid = Number(x?.template_id) || 0;
    if (!Number.isFinite(sid) || sid <= 0) return;
    if (!Number.isFinite(tid) || tid <= 0) return;

    if (!m.has(sid)) m.set(sid, new Set());
    m.get(sid).add(tid);
  });

  return m; // Map<number, Set<number>>
});

const usedSubjectIdsInLetter = computed(() => {
  const tbs = Array.isArray(letterTemplatesBySubject.value) ? letterTemplatesBySubject.value : [];
  const set = new Set();
  tbs.forEach(x => {
    const sid = Number(x?.subject_id);
    if (Number.isFinite(sid) && sid > 0) set.add(sid);
  });
  return Array.from(set);
});

function hasNonEmptyJson(val) {
  if (val == null) return false;
  if (typeof val === 'string') {
    const t = val.trim();
    if (!t) return false;
    try {
      const o = JSON.parse(t);
      return o && typeof o === 'object' ? Object.keys(o).length > 0 : !!t;
    } catch {
      return true; // nicht-leerer String
    }
  }
  if (typeof val === 'object') return Object.keys(val).length > 0;
  return true;
}

// Baut die Status-Timeline: [V] [Fach1] [Fach2] ... [R]
function buildStatusBoxes(student) {
  const sid = Number(student?.id);
  const boxes = [];

  // Vorblatt
  const fJson = student?.front_sheet_json;
  let fCls = 'empty';
  const hasFront = hasNonEmptyJson(fJson);
  if (hasFront) fCls = 'created';
  boxes.push({
    kind: 'front',
    label: 'VB',
    cls: fCls,
    title: hasFront ? 'Vorblatt: vorhanden' : 'Vorblatt: leer'
  });

  // Fach-Kästchen (mittig, Reihenfolge gemäß Sort)
  const allAssessments = (Array.isArray(letterAssessmentStatuses.value) && letterAssessmentStatuses.value.length > 0)
      ? letterAssessmentStatuses.value
      : (Array.isArray(assessments.items) ? assessments.items : []);

  subjectsSorted.value.forEach(subj => {
    const subId = Number(subj?.id);
    const abbr = subjectAbbrMap.value.get(subId) || '';
    const subjTitle = String(subj?.title || '').trim();

    const templateIdsSet = templateIdsBySubjectMap.value.get(subId) || new Set();
    const hasTemplate = templateIdsSet.size > 0;

    let cls = 'empty';
    let titleBase = 'Keine Vorlage';

    if (hasTemplate) {
      cls = 'templated';
      titleBase = 'Vorlage vorhanden';

      const candidates = allAssessments.filter(x => {
        const sId = Number(x?.student_id ?? x?.studentId ?? 0);
        const tId = Number(x?.template_id ?? x?.templateId ?? 0);
        return sId === sid && templateIdsSet.has(tId);
      });

      const anyReleased = candidates.some(c => Boolean(c?.released));

      if (candidates.length > 0) {
        if (anyReleased) {
          cls = 'released';
          titleBase = 'Raster freigegeben';
        } else {
          cls = 'created templated';
          titleBase = 'Raster vorhanden';
        }
      }
    }

    const title = subjTitle ? `${titleBase} (${subjTitle})` : titleBase;

    boxes.push({
      kind: 'subject',
      label: abbr,
      cls,
      title
    });
  });

  // Rückblatt
  const bJson = student?.back_sheet_json;
  let bCls = 'empty';
  const hasBack = hasNonEmptyJson(bJson);
  if (hasBack) bCls = 'created';
  boxes.push({
    kind: 'back',
    label: 'RB',
    cls: bCls,
    title: hasBack ? 'Rückblatt: vorhanden' : 'Rückblatt: leer'
  });

  return boxes;
}

const TIMELINE_BOX = 28; // px – Breite/Höhe der Kästchen
const TIMELINE_GAP = 10; // px – Horizontaler Abstand zwischen Kästchen

function getTrackStyleFor() {
  const n = Number(subjectsSorted.value?.length || 0);
  if (!Number.isFinite(n) || n <= 0) {
    return {display: 'none'};
  }
  const width = (n * TIMELINE_BOX) + ((n - 1) * TIMELINE_GAP);
  // Track mittig unter den Subject-Kästchen ausrichten (CSS setzt translate(-50%, -50%))
  return {width: `${width}px`};
}

// NEU: sorgt dafür, dass Header+Body dieselbe (nicht schrumpfende) Spaltenbasis haben
function getStudentsTableVars() {
  const subjectsCount = Math.max(0, Number(subjectsSorted.value?.length || 0));

  // Status besteht aus: VB (32) + Subjects (28 je) + RB (32) + Gaps (10)
  const frontBackWidth = 32 + 32;
  const subjectsWidth = subjectsCount * TIMELINE_BOX;
  const boxesCount = subjectsCount + 2; // VB + ... + RB
  const gapsWidth = Math.max(0, boxesCount - 1) * TIMELINE_GAP;

  const statusMin = frontBackWidth + subjectsWidth + gapsWidth;

  // Gesamttabelle: Schüler(220) + Status + Aktionen(180) + Grid-Gaps(2*8) + Padding links/rechts (2*7)
  const tableMin = 220 + statusMin + 180 + (2 * 8) + (2 * 7);

  return {
    '--students-status-min': `${statusMin}px`,
    '--students-table-min': `${tableMin}px`,
  };
}

function onStudentPdf(student) {
  const sid = Number(student?.id);
  if (!Number.isFinite(sid) || sid <= 0) return;

  const letterId = Number(selectedLetter.value?.id) || 0;
  if (letterId <= 0) {
    toast.add({severity: 'error', summary: 'Kein Lernbrief ausgewählt.', life: 3000});
    return;
  }

  const parseMaybeJson = (val) => {
    if (!val) return {};
    if (typeof val === 'object') return val;
    if (typeof val === 'string') {
      try {
        const o = JSON.parse(val);
        return (o && typeof o === 'object') ? o : {};
      } catch {
        return {};
      }
    }
    return {};
  };

  const front = parseMaybeJson(student?.front_sheet_json);
  const back = parseMaybeJson(student?.back_sheet_json);
// NEU: Name-Override priorisieren (DB-Spalte), sonst Standardname
  const studentNameForLetter = String(student?.student_name_override ?? '').trim()
      || displayStudentName(student);
  const subjectOrder = (Array.isArray(subjectsSorted.value) ? subjectsSorted.value : [])
      .map(s => String(s?.title ?? '').trim())
      .filter(Boolean);

  listReleasedAssessmentsForStudent({studentId: sid, feedbackLetterId: letterId})
      .then((res) => {
        const items = Array.isArray(res?.items) ? res.items : [];
        const ids = items
            .map(r => Number(r?.id))
            .filter(n => Number.isFinite(n) && n > 0);

        if (!ids.length) {
          toast.add({severity: 'info', summary: 'Keine freigegebenen Raster für diesen Schüler.', life: 3000});
          return;
        }

        return exportFeedbackLetterPdf({
          student: studentNameForLetter,
          frontSheet: front,
          backSheet: back,
          assessmentIds: ids,
          assessments: items,
          subjectOrder,
          footnote: selectedLetter.value?.footnote ?? selectedLetter.value?.footnote_text ?? selectedLetter.value?.foot_note ?? 'feedback_letters.footnote'
        });
      })
      .catch((e) => {
        console.error('PDF-Assessment-Liste konnte nicht geladen werden', e);
        toast.add({severity: 'error', summary: 'Raster konnten nicht geladen werden.', life: 3500});
      });
}

async function onEditFront(student) {
  const sid = Number(student?.id);
  if (!Number.isFinite(sid) || sid <= 0) return;

  // Basis aus vorhandenem JSON übernehmen
  let base = {};
  let raw = student?.front_sheet_json;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch { /* ignore */
    }
  }
  if (raw && typeof raw === 'object') {
    base = {...raw};
  }

  const overrideName = String(student?.student_name_override ?? '').trim();

  frontDlg.student = student || null;
  frontDlg.initial = null;
  frontDlg.loading = true;
  frontDlg.open = true;

  // Studentendaten + aktuelles Schuljahr nachladen
  try {
    const [stRes, syRes] = await Promise.allSettled([
      api.get(`/students/${sid}`).then(r => r.data),
      getCurrentSchoolYear()
    ]);

    const st = stRes.status === 'fulfilled'
        ? (stRes.value?.student || stRes.value || {})
        : {};

    const birthDate = st.birth_date ?? st.date_of_birth ?? st.birthdate ?? '';
    const classLevel = st.class_level ?? st.grade ?? st.classLevel ?? '';

    let currentSy = '';
    if (syRes.status === 'fulfilled') {
      const sy = syRes.value || {};
      currentSy = String(
          sy.title ?? sy.name ?? sy.label ?? sy.code ?? sy.school_year ?? ''
      ).trim();
    }

    frontDlg.initial = {
      studentname: overrideName || (base.studentname ?? ''),
      birth_date: base.birth_date ?? (birthDate ? String(birthDate).slice(0, 10) : ''),
      place: base.place ?? '',
      class_level: base.class_level ?? (classLevel !== undefined ? String(classLevel) : ''),
      school_year: base.school_year ?? currentSy,
      notes: base.notes ?? ''
    };
  } catch (e) {
    frontDlg.initial = {
      studentname: overrideName || (base.studentname ?? ''),
      birth_date: base.birth_date ?? '',
      place: base.place ?? '',
      class_level: base.class_level ?? '',
      school_year: base.school_year ?? '',
      notes: base.notes ?? ''
    };
  } finally {
    frontDlg.loading = false;
  }
}

async function onFrontSaved({studentId, data}) {
  const sid = Number(studentId);
  const lid = Number(selectedLetter.value?.id);
  if (!Number.isFinite(sid) || !Number.isFinite(lid) || lid <= 0 || sid <= 0) {
    toast.add({severity: 'error', summary: 'Ungültige IDs – Speichern abgebrochen.', life: 3500});
    return;
  }

  try {
    const res = await setLetterFrontSheet(lid, sid, data);

    const all = Array.isArray(letterStudents.all) ? letterStudents.all : [];
    const row = all.find(x => Number(x?.id) === sid);
    if (row) {
      row.front_sheet_json = res?.front_sheet_json ?? {...data};

      // NEU: Override-Name im lokalen Zustand übernehmen
      if (Object.prototype.hasOwnProperty.call(res || {}, 'student_name_override')) {
        row.student_name_override = res?.student_name_override;
      } else {
        const name = String(data?.studentname ?? '').trim();
        row.student_name_override = name ? name : null;
      }
    }

    // Tab 2 aktualisieren
    await loadLetterStudents(letterStudents.page || 1);

    // Tab 4 (Kompetenzraster) aktualisieren, damit row.student neu vom Backend kommt
    await loadAssessments(assessments.page || 1);

    toast.add({severity: 'success', summary: 'Vorblatt gespeichert.', life: 3000});
  } catch (e) {
    console.error('Vorblatt speichern fehlgeschlagen', e);
    toast.add({severity: 'error', summary: 'Vorblatt konnte nicht gespeichert werden.', life: 4000});

    return;
  } finally {
    frontDlg.open = false;
    frontDlg.student = null;
    frontDlg.initial = null;
  }
}

function onEditBack(student) {
  const sid = Number(student?.id);
  if (!Number.isFinite(sid) || sid <= 0) return;

  // Vorhandene Daten übernehmen (JSON oder Objekt)
  let base = {};
  let raw = student?.back_sheet_json;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch { /* ignore */
    }
  }
  if (raw && typeof raw === 'object') base = {...raw};

  backDlg.student = student || null;
  backDlg.initial = {
    additional_activities: base.additional_activities ?? '',
    remarks: base.remarks ?? '',
    absences: base.absences ?? '',
    learning_times: base.learning_times ?? '',
    tardiness: base.tardiness ?? '',
    date: base.date ?? '',
    unexcused_absences: base.unexcused_absences ?? '',
    unexcused_learning_times: base.unexcused_learning_times ?? '',
  };
  backDlg.open = true;
}

async function onBackSaved({studentId, data}) {
  const sid = Number(studentId);
  const lid = Number(selectedLetter.value?.id);
  if (!Number.isFinite(sid) || !Number.isFinite(lid) || lid <= 0 || sid <= 0) {
    toast.add({severity: 'error', summary: 'Ungültige IDs – Speichern abgebrochen.', life: 3500});
    return;
  }

  try {
    const res = await setLetterBackSheet(lid, sid, data);
    const all = Array.isArray(letterStudents.all) ? letterStudents.all : [];
    const row = all.find(x => Number(x?.id) === sid);
    if (row) {
      row.back_sheet_json = res?.back_sheet_json ?? {...data};
      row.back_released = Boolean(res?.back_released ?? false);
    }
    toast.add({severity: 'success', summary: 'Rückblatt gespeichert.', life: 3000});
  } catch (e) {
    console.error('Rückblatt speichern fehlgeschlagen', e);
    toast.add({severity: 'error', summary: 'Rückblatt konnte nicht gespeichert werden.', life: 4000});
    return;
  } finally {
    backDlg.open = false;
    backDlg.student = null;
    backDlg.initial = null;
  }
}

// Rollenabhängig: Darf „Öffnen“ angezeigt werden?
function canOpenLetter(row) {
  return Boolean(row?.can_open ?? row?.canOpen ?? false);
}

// Final-Status ermitteln (robust gegen unterschiedliche Feldnamen)
function rowFinalized(row) {
  const r = row || {};
  return Boolean(r.finalized ?? r.is_finalized ?? r.isFinalized ?? r.final ?? false);
}

// Final-Status des aktuell ausgewählten Lernbriefs
const isSelectedLetterFinalized = computed(() => rowFinalized(selectedLetter.value));

// Final-Status umschalten (Tab 1: Toggle-Button)
async function toggleLetterFinal(row) {
  const id = Number(row?.id);
  if (!Number.isFinite(id) || id <= 0) return;

  const next = !rowFinalized(row);
  try {
    await finalizeLetter(id, next);

    // Liste aktualisieren
    await loadLetters(letters.page || 1);

    // Falls der geöffnete Lernbrief betroffen ist: Zustand aktualisieren
    if (selectedLetter.value && Number(selectedLetter.value.id) === id) {
      selectedLetter.value = {...selectedLetter.value, finalized: next};
    }

    toast.add({
      severity: 'success',
      summary: next ? 'Lernbrief als final markiert.' : 'Final-Status zurückgenommen.',
      life: 3000
    });
  } catch (e) {
    const msg = e?.response?.data?.error?.message
        || e?.response?.data?.message
        || e?.message
        || 'Final-Status konnte nicht geändert werden.';
    toast.add({severity: 'error', summary: msg, life: 4000});
  }
}

// Lädt im Hintergrund für die aktuell sichtbaren Lernbriefe die Anzahl der zugewiesenen Schüler
// Beim Wechsel auf Tab 2: Details gezielt nachladen
const prefetchedForDetail = ref(false);

async function enterLetterDetailTab() {
  const lid = Number(selectedLetter.value?.id);
  if (!Number.isFinite(lid) || lid <= 0) return;

  try {
    const data = await getLetterDetail(lid);

    const det = data?.letter ?? data ?? {};
    const students = Array.isArray(data?.students) ? data.students : [];
    const subjects = Array.isArray(data?.subjects)
        ? data.subjects
        : (Array.isArray(det?.subjects) ? det.subjects : []);
    const tbs = Array.isArray(data?.templates_by_subject) ? data.templates_by_subject : [];

    selectedLetter.value = {...selectedLetter.value, ...det};
    letterAssignedCounts.set(lid, students.length);
    letterSubjectCounts.set(lid, subjects.length);

    // Tab-2-Schülerliste direkt aus dem konsolidierten Response
    letterStudents.all = students;
    letterStudents.total = students.length;
    letterStudents.pages = Math.max(1, Math.ceil(students.length / (Number(letterStudentsPageSize.value) || 20)));
    letterStudents.page = Math.min(Math.max(1, Number(letterStudents.page) || 1), letterStudents.pages);

    // Fächer + Template-Mapping für Timeline
    letterSubjects.value = Array.isArray(subjects)
        ? [...subjects].sort((a, b) => (Number(a.sort ?? 0) - Number(b.sort ?? 0)))
        : [];
    letterTemplatesBySubject.value = tbs;

    // Kompakte Assessment-Statusliste für Timeline
    // Backend kann hier Array ODER Objekt {"0": {...}} liefern → normalisieren
    const rawAssessments = data?.assessments ?? [];
    const normalized = Array.isArray(rawAssessments)
        ? rawAssessments
        : (rawAssessments && typeof rawAssessments === 'object' ? Object.values(rawAssessments) : []);

    letterAssessmentStatuses.value = (Array.isArray(normalized) ? normalized : []).map(a => ({
      ...a,
      released: Boolean(a?.released),
      exists: Boolean(a?.exists ?? true),
      student_id: Number(a?.student_id),
      template_id: Number(a?.template_id),
    }));

  } catch (e) {
    console.warn('Lernbrief-Details (/detail) neu laden fehlgeschlagen', e);
    // Reset, damit kein Mischzustand angezeigt wird
    letterStudents.all = [];
    letterStudents.total = 0;
    letterStudents.pages = 1;
    letterStudents.page = 1;
    letterAssessmentStatuses.value = [];
  }
}

watch(activeTab, async (val) => {
  if (val === 'letterDetail') {
    if (prefetchedForDetail.value) {
      // openLetter hat Tabwechsel bereits mit frischen Daten vorbereitet
      prefetchedForDetail.value = false;
      return;
    }
    await enterLetterDetailTab();
  }
});

onMounted(() => {
  reloadAll(1);
});
</script>

<style scoped>
/* Tabs */
.tabs {
  display: flex;
  gap: 6px;
  align-items: center;
  border-bottom: 1px solid var(--border);
  margin-bottom: 12px;
  margin-top: 25px;
}

.tabs.full-width {
  width: 100%;
}

.tab {
  height: 36px;
  padding: 0 12px;
  border: 1px solid transparent;
  border-radius: 6px 6px 0 0;
  background: var(--surface);
  cursor: pointer;
  color: var(--text);
  font-weight: 500;
}

.tab.active {
  background: antiquewhite;
  border-color: var(--border);
  border-bottom-color: antiquewhite;
}

.tab.disabled,
.tab:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
}

h3 {
  margin: 0;
}

.section-title {
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  margin: 0 0 12px 0;
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.col {
  min-width: 0;
}

.subheader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.filters {
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr 180px 60px;
  width: 100%;
  font-size: 14px;
}

.filters.letters {
  grid-template-columns: 1fr 60px;
}

.filters.templates {
  grid-template-columns: 1fr 180px 180px 220px 65px;
}

.filters.assessments {
  grid-template-columns: 1fr 200px 160px 200px 60px;
}

.input {
  height: 36px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
}

.table {
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: hidden;
}

.thead,
.tr {
  display: grid;
  grid-template-columns: 1fr 110px 75px 130px;
  gap: 8px;
  padding: 7px;
  border-bottom: 1px solid var(--border);
  font-size: 13px;
  align-items: center;
}

/* Templates-Tabelle: 6 Spalten (Thema, Notiz, Fach, Lehrer, Erstellt, Aktionen) */
.thead-templates,
.tr-templates {
  grid-template-columns: 1fr 220px 220px 120px 100px 150px;
}

.thead {
  background: var(--bg);
  color: var(--surface-600);
  font-size: 13px;
  font-weight: 600;
}

/* Anpassungen für die Lernbriefe-Tabelle */
.thead-letters,
.tr-letters {
  grid-template-columns: 1fr 120px 180px 180px;
}

/* NEU: Schüler-Tabelle soll lieber scrollen statt quetschen */
.table.table-students {
  -webkit-overflow-scrolling: touch;
}

.thead-students,
.tr-students {
  grid-template-columns: 220px minmax(var(--students-status-min, 0px), auto) 180px;
  min-width: var(--students-table-min, 0px);
}

/* verhindert, dass der Header auf schmalen Screens umbricht/zusammenschiebt */
.thead-students > div {
  white-space: nowrap;
}

/* optional: Aktionen-Spalte soll nicht in mehrere Zeilen umbrechen */
.tr-students .actions {
  flex-wrap: nowrap;
}

.tr.tr-students {
  padding: 3px 7px;
}

/* Zebra-Striping */
.table .tr:nth-child(odd) {
  background: color-mix(in srgb, var(--bg) 55%, var(--surface));
}

.table .tr:nth-child(even) {
  background: var(--surface);
}

/* Hover */
.table .tr:hover {
  background: color-mix(in srgb, var(--primary) 8%, var(--surface));
}

.thead-assessments,
.tr-assessments {
  grid-template-columns: 1fr 220px 180px 120px 140px 80px 100px;
}

.tr:last-child {
  border-bottom: none;
}

.actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}

.btn {
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 6px;
  cursor: pointer;
}

.btn.icon-only {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn.icon-only .pi {
  pointer-events: none;
}

.btn.icon-only:disabled {
  border-color: var(--border);
  color: var(--border);
  background: var(--bg);
}

.btn:hover {
  background: color-mix(in srgb, var(--primary) 8%, transparent);
}

.btn.danger {
  border-color: color-mix(in srgb, var(--danger) 60%, var(--border));
  color: var(--danger);
}

.btn-primary {
  border: 1px solid var(--primary);
  background: var(--primary);
  color: #fff;
}

.btn-primary:hover {
  background: var(--primary-600);
}

.btn-success {
  border-color: color-mix(in srgb, var(--primary) 60%, var(--border));
  color: var(--primary);
}

.btn-success:hover {
  background: color-mix(in srgb, var(--primary) 35%, #fff);
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.pagination-left {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 160px;
}

.pagination-right {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.pagination-center {
  display: inline-flex;
  align-items: center;
  justify-content: end;
  gap: 10px;
}

.page-size {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.input-select {
  padding-right: 10px;
}

.meta {
  color: var(--muted);
  font-size: 12px;
}

/* Badge: freigegeben */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 1px 6px;
  font-size: 11px;
  margin-left: 6px;
}

.badge-success {
  border-color: color-mix(in srgb, var(--success) 55%, var(--border));
  background: color-mix(in srgb, var(--success) 10%, var(--surface));
  color: var(--success);
}

.badge-info {
  border-color: color-mix(in srgb, var(--info) 55%, var(--border));
  background: color-mix(in srgb, var(--info) 10%, var(--surface));
  color: var(--info);
}

.badge-warn {
  border-color: color-mix(in srgb, var(--warn) 55%, var(--border));
  background: color-mix(in srgb, var(--warn) 10%, var(--surface));
  color: var(--warn);
}

/* Status-Timeline (Kästchen + Track) */
.status-timeline {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 36px;
  padding: 0;
}

.status-timeline .track {
  position: absolute;
  top: 50%;
  left: 50%;
  height: 2px;
  transform: translate(-50%, -50%);
  background: var(--border);
  z-index: 0;
  width: 100%; /* Fallback, wird per :style überschrieben */
  max-width: 100%;
}

.status-timeline .box {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--surface);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  color: var(--text);
  z-index: 1; /* über Track */
}

.status-timeline .box .abbr {
  line-height: 1;
}

/* Zustände:
   - empty: weiß
   - templated: grau
   - created: grau + grüner Rand
   - released: grüner Hintergrund + grüner Rand
*/
.status-timeline .box.templated, .status-timeline .box.created {
  background: color-mix(in srgb, #ddd 50%, var(--surface));
}

.status-timeline .box.created {
  border-color: color-mix(in srgb, var(--success) 85%, var(--border));
}

.status-timeline .box.released, .status-timeline .box.front.created, .status-timeline .box.back.created {
  background: color-mix(in srgb, var(--success) 35%, var(--surface));
  border-color: color-mix(in srgb, var(--success) 55%, var(--border));
  color: var(--success);
}

.status-timeline .box.front, .status-timeline .box.back {
  height: 32px;
  width: 32px;
  border-width: 1px;
}

.empty {
  padding: 12px;
  color: var(--muted);
}

.status-timeline .box.subject.empty {
  border-style: dashed;
}

.checkbox-col {
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 1024px) {
  .two-col {
    grid-template-columns: 1fr;
  }
}
</style>